import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StoredSession, MAX_SESSIONS } from './session.model';
import { JwtService, JwtPayload } from '../jwt/jwt.service';
import { AuthStateService } from '../auth-state/auth-state-service.service';
import { UserService } from '../user/user.service';
import { DynamicRouteService } from '../dynamic-route/dynamic-route.service';
import { TIME_CONSTANTS, TimeUtils } from '../../constants/time.constants';

const SESSIONS_KEY = 'sessions';
const ACTIVE_INDEX_KEY = 'active_session_index';

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService {
  private sessionsSubject = new BehaviorSubject<StoredSession[]>(this.loadSessions());
  sessions$ = this.sessionsSubject.asObservable();

  private activeIndexSubject = new BehaviorSubject<number>(this.loadActiveIndex());
  activeIndex$ = this.activeIndexSubject.asObservable();

  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);
  sessionExpired$ = this.sessionExpiredSubject.asObservable();

  constructor(
    private jwtService: JwtService,
    private authStateService: AuthStateService,
    private userService: UserService,
    private dynamicRouteService: DynamicRouteService,
    private injector: Injector
  ) {}

  // --- Read ---

  getSessions(): StoredSession[] {
    return this.loadSessions();
  }

  getActiveIndex(): number {
    return this.loadActiveIndex();
  }

  getActiveSession(): StoredSession | null {
    const sessions = this.loadSessions();
    const index = this.loadActiveIndex();
    return sessions[index] || null;
  }

  hasSessionForEmail(email: string): boolean {
    return this.loadSessions().some(s => s.email.toLowerCase() === email.toLowerCase());
  }

  getDeviceTokenForEmail(email: string): string | null {
    const session = this.loadSessions().find(s => s.email.toLowerCase() === email.toLowerCase());
    return session?.device_token || null;
  }

  getSessionCount(): number {
    return this.loadSessions().length;
  }

  updateActiveSessionImg(img: string): void {
    const sessions = this.loadSessions();
    const index = this.loadActiveIndex();
    if (sessions[index]) {
      sessions[index].img = img;
      this.saveSessions(sessions);
    }
  }

  // --- Mutations ---

  addSession(token: string, deviceToken: string | null, email: string): StoredSession {
    const sessions = this.loadSessions();
    if (sessions.length >= MAX_SESSIONS) {
      throw new Error('Max sessions reached');
    }

    const payload = this.jwtService.parseJwt(token);
    const session = this.buildSession(email, token, deviceToken, payload);

    // Deduplicate: replace existing session with same email
    const existingIndex = sessions.findIndex(s => s.email.toLowerCase() === email.toLowerCase());
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
      this.saveSessions(sessions);
      this.syncMirrorKeys(session);
      this.setActiveIndex(existingIndex);
      return session;
    }

    sessions.push(session);
    this.saveSessions(sessions);
    this.syncMirrorKeys(session);
    this.setActiveIndex(sessions.length - 1);
    return session;
  }

  replaceActiveSession(token: string, deviceToken: string | null, email: string): StoredSession {
    const payload = this.jwtService.parseJwt(token);
    const session = this.buildSession(email, token, deviceToken, payload);

    const sessions = this.loadSessions();
    const activeIndex = this.loadActiveIndex();

    if (sessions.length === 0) {
      // First login ever
      this.saveSessions([session]);
      this.setActiveIndex(0);
    } else {
      sessions[activeIndex] = session;
      this.saveSessions(sessions);
    }

    this.syncMirrorKeys(session);
    return session;
  }

  updateSession(index: number, token: string, deviceToken: string | null): void {
    const sessions = this.loadSessions();
    if (!sessions[index]) return;

    const payload = this.jwtService.parseJwt(token);
    sessions[index] = {
      ...sessions[index],
      access_token: token,
      device_token: deviceToken ?? sessions[index].device_token,
      ...this.extractFieldsFromPayload(payload),
    };
    this.saveSessions(sessions);

    if (index === this.loadActiveIndex()) {
      this.syncMirrorKeys(sessions[index]);
    }
  }

  switchToSession(index: number): boolean {
    const sessions = this.loadSessions();
    const target = sessions[index];
    if (!target) return false;

    const payload = this.jwtService.parseJwt(target.access_token);
    if (this.jwtService.isTokenExpired(payload.exp)) {
      return false;
    }

    this.syncMirrorKeys(target);
    this.setActiveIndex(index);
    this.clearAccountState();
    this.clearServiceCaches();

    this.dynamicRouteService.clearDynamicRoutes();
    const routes = this.authStateService.getRoutesItems();
    this.dynamicRouteService.configureDynamicRoutes(routes);

    return true;
  }

  removeActiveSession(): void {
    const sessions = this.loadSessions();
    const activeIndex = this.loadActiveIndex();

    if (sessions.length === 0) return;

    // Call logout for the active session's JWT
    sessions.splice(activeIndex, 1);
    this.saveSessions(sessions);

    if (sessions.length > 0) {
      const newIndex = Math.min(activeIndex, sessions.length - 1);
      this.setActiveIndex(newIndex);
      this.syncMirrorKeys(sessions[newIndex]);
    } else {
      this.setActiveIndex(0);
      this.clearMirrorKeys();
    }
  }

  removeActiveSessionSilent(): void {
    this.removeActiveSession();
  }

  emitSessionExpired(): void {
    this.sessionExpiredSubject.next(true);
  }

  // --- Private helpers ---

  private buildSession(email: string, token: string, deviceToken: string | null, payload: JwtPayload): StoredSession {
    const expires_in = TimeUtils.addDays(new Date(), 1);
    return {
      email,
      access_token: token,
      device_token: deviceToken,
      ...this.extractFieldsFromPayload(payload),
      expires_in: expires_in.getTime().toString(),
      added_at: Date.now(),
    };
  }

  private extractFieldsFromPayload(payload: JwtPayload): Pick<StoredSession, 'name' | 'role' | 'account_type' | 'account_name' | 'account_reference' | 'user_reference' | 'assigned_smtp' | 'smtp_from'> {
    return {
      name: payload.name,
      role: payload.role,
      account_type: payload.account_type,
      account_name: payload.account_name,
      account_reference: payload.account_reference,
      user_reference: payload.user_reference,
      assigned_smtp: payload.assigned_smtp || '',
      smtp_from: payload.smtp_from || '',
    };
  }

  private syncMirrorKeys(session: StoredSession): void {
    localStorage.setItem('access_token', session.access_token);
    localStorage.setItem('expires_in', session.expires_in);
    localStorage.setItem('name', session.name);
    localStorage.setItem('role', session.role);
    localStorage.setItem('account_type', session.account_type);
    localStorage.setItem('account_name', session.account_name);
    localStorage.setItem('account_reference', session.account_reference);
    localStorage.setItem('user_reference', session.user_reference);
    localStorage.setItem('assigned_smtp', session.assigned_smtp);
    localStorage.setItem('smtp_from', session.smtp_from);
  }

  private clearMirrorKeys(): void {
    const keys = [
      'access_token', 'expires_in', 'name', 'role', 'account_type',
      'account_name', 'account_reference', 'user_reference',
      'assigned_smtp', 'smtp_from',
    ];
    keys.forEach(k => localStorage.removeItem(k));
  }

  private clearAccountState(): void {
    const keys = [
      'searchParams', 'searchParamsCalendar', 'searchParamsCampaign',
      'searchParamsClients', 'searchParamsContracts', 'searchParamsInvoice',
      'searchParamsMyInbox', 'searchParamsNotes', 'searchParamsPrivateNotes',
      'searchParamsStockCampaign', 'searchParamsExpandedCampaign',
      'searchParamsAutomation',
      'sortColumn', 'sortDirection', 'per_page', 'user', 'ladata',
    ];
    keys.forEach(k => localStorage.removeItem(k));
  }

  private clearServiceCaches(): void {
    this.userService.clearUser();
    this.authStateService.setSelectedSupplier(null);

    // Lazy-resolve via Injector to break circular DI chain
    // (these services use HttpClient → AuthInterceptor → SessionManagerService)
    try {
      const { UsersService } = require('../users/users.service');
      const { OrdersStateService } = require('../orders/orders-state.service');
      const { OrdersAlertService } = require('../orders/orders-alert.service');
      const { OrdersSelectionService } = require('../orders/orders-selection.service');
      const { OrdersService } = require('../orders/orders.service');
      const { DataService } = require('../orders/data.service');

      this.injector.get(UsersService)?.updateUserImage?.(null);
      this.injector.get(OrdersStateService)?.clear?.();
      this.injector.get(OrdersAlertService)?.reset?.();
      this.injector.get(OrdersSelectionService)?.clearSelection?.();
      this.injector.get(DataService)?.clear?.();

      const os = this.injector.get(OrdersService);
      os?.updateActionRequiredCount?.(0);
      os?.updateActionRequiredCount2?.(0);
      os?.updatePrivateActionRequiredCount?.(0);
    } catch (e) {
      console.warn('[SessionManager] clearServiceCaches partial failure:', e);
    }
  }

  private loadSessions(): StoredSession[] {
    try {
      const raw = localStorage.getItem(SESSIONS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private saveSessions(sessions: StoredSession[]): void {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    this.sessionsSubject.next(sessions);
  }

  private loadActiveIndex(): number {
    return parseInt(localStorage.getItem(ACTIVE_INDEX_KEY) || '0', 10);
  }

  private setActiveIndex(index: number): void {
    localStorage.setItem(ACTIVE_INDEX_KEY, String(index));
    this.activeIndexSubject.next(index);
  }
}
